import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CircuitBoard, Activity, Link2, Github, Aperture, Hand, Truck, Globe, Cpu } from 'lucide-react';
import './FeaturedProjects.css';

const projects = [
  {
    id: 'smart-campus',
    title: 'AI Smart Campus',
    subtitle: 'AI/ML',
    description: 'An intelligent campus management system using computer vision and IoT sensors to optimize resource utilization and enhance security.',
    technologies: ['Python', 'TensorFlow', 'React', 'Node.js'],
    category: 'AI/ML',
    github: 'https://github.com/aiec/smart-campus',
    icon: <CircuitBoard />,
    color: '#0ea5e9'
  },
  {
    id: 'blockchain-voting',
    title: 'Secure Choice',
    subtitle: 'Blockchain Voting',
    description: 'A decentralized voting platform ensuring transparency, security, and immutability in electoral processes using advanced blockchain technology.',
    technologies: ['Solidity', 'Web3.js', 'React', 'Node.js'],
    category: 'Blockchain',
    github: 'https://github.com/aiec/voting-system',
    icon: <Link2 />,
    color: '#8b5cf6'
  },
  {
    id: 'energy-monitor',
    title: 'Real-time Energy Monitor',
    subtitle: 'IoT Monitor',
    description: 'IoT-based energy consumption monitoring system with predictive analytics and automated optimization recommendations.',
    technologies: ['IoT', 'Python', 'React', 'AWS'],
    category: 'IoT',
    github: 'https://github.com/aiec/energy-monitor',
    icon: <Activity />,
    color: '#f59e0b'
  },
  {
    id: 'image-caption-generator',
    title: 'Image Caption Generator',
    subtitle: 'AI/ML',
    description: 'Deep learning model that automatically generates descriptive captions for images using CNN and RNN architectures.',
    technologies: ['Python', 'TensorFlow', 'OpenCV', 'Flask'],
    category: 'AI/ML',
    github: 'https://github.com/RishitLuthra/Image-Caption-Generator',
    icon: <Aperture />,
    color: '#ec4899'
  },
  {
    id: 'sign-language-translator',
    title: 'Sign Language Translator',
    subtitle: 'AI/ML',
    description: 'Computer vision system that translates sign language gestures into text and speech in real-time using MediaPipe.',
    technologies: ['Python', 'MediaPipe', 'OpenCV', 'TensorFlow'],
    category: 'AI/ML',
    github: 'https://github.com/alexpardon7860/Real-time-Sign-Language-Translator-Virtual-Camera-main',
    icon: <Hand />,
    color: '#10b981'
  },
  {
    id: 'hand-sign-recognition',
    title: 'Hand Sign Recognition',
    subtitle: 'AI/ML',
    description: 'Real-time hand sign recognition and verbalization system to help muted individuals communicate through text and speech.',
    technologies: ['Python', 'MediaPipe', 'Random Forest', 'Speech API'],
    category: 'AI/ML',
    github: 'https://github.com/IshanKandari/real-time-hand-sign-recognition-and-verbalization',
    icon: <Hand />,
    color: '#0f766e'
  },
  {
    id: 'driveworth',
    title: 'DriveWorth Automotive',
    subtitle: 'Web Dev',
    description: 'A comprehensive platform for vehicle valuation and automotive services with modern web technologies.',
    technologies: ['React', 'Node.js', 'MongoDB', 'Express'],
    category: 'Web Dev',
    github: 'https://github.com/GunmeetBhatia/DriveWorth',
    icon: <Truck />,
    color: '#38bdf8'
  },
  {
    id: 'world-tour',
    title: 'World Tour Planner',
    subtitle: 'Web Dev',
    description: 'Interactive travel planning application built with React and Vite for exploring destinations worldwide.',
    technologies: ['React', 'Vite', 'JavaScript', 'CSS'],
    category: 'Web Dev',
    github: 'https://github.com/mankiratsingh123/World-Tour',
    icon: <Globe />,
    color: '#818cf8'
  },
  {
    id: 'quantum-simulator',
    title: 'Quantum Simulator',
    subtitle: 'Quantum',
    description: 'Educational quantum computing simulator for learning quantum algorithms and circuit design.',
    technologies: ['Python', 'Qiskit', 'React', 'D3.js'],
    category: 'Quantum',
    github: '#',
    icon: <Cpu />,
    color: '#c084fc'
  }
];

const AUTO_ADVANCE_DELAY = 2 * 60 * 1000;
const MOBILE_BREAKPOINT = 840;

const FeaturedProjects = () => {
  const [activeProject, setActiveProject] = useState(0);
  const [isSelectionLocked, setIsSelectionLocked] = useState(false);
  const [isMobile, setIsMobile] = useState(() => (
    typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT
  ));
  const containerRef = useRef(null);

  useEffect(() => {
    const updateViewport = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);

    updateViewport();
    window.addEventListener('resize', updateViewport);
    return () => window.removeEventListener('resize', updateViewport);
  }, []);

  useEffect(() => {
    if (isSelectionLocked) {
      return undefined;
    }

    const timer = setInterval(() => {
      setActiveProject((prev) => (prev + 1) % projects.length);
    }, AUTO_ADVANCE_DELAY);

    return () => clearInterval(timer);
  }, [isSelectionLocked]);

  const selectProject = (index) => {
    setActiveProject(index);
    setIsSelectionLocked(true);
  };

  const streamLines = useMemo(() => (
    [...Array(6)].map((_, i) => (
      <div
        key={`stream-${i}`}
        className="stream-line"
        style={{ top: `${i * 20}%`, animationDelay: `${i * 2}s`, opacity: 0.1 + (i * 0.05) }}
      />
    ))
  ), []);

  const particles = useMemo(() => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    return [...Array(isMobile ? 6 : 12)].map((_, i) => ({
      id: i,
      x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1000),
      y: Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 1000),
      duration: 10 + Math.random() * 10,
      width: 2 + Math.random() * 4,
      height: 2 + Math.random() * 4,
      yAnim: [null, Math.random() * -100, Math.random() * 100]
    }));
  }, []);

  return (
    <section
      className="featured-projects-section"
      id="projects"
      ref={containerRef}
    >
      <div className="atmospheric-core" />
      <div className="data-streams">{streamLines}</div>
      {particles.map((p) => (
        <motion.div
          key={`particle-${p.id}`}
          className="particle-slab"
          initial={{ x: p.x, y: p.y, opacity: 0 }}
          animate={{ y: p.yAnim, opacity: [0, 0.5, 0] }}
          transition={{ duration: p.duration, repeat: Infinity, ease: 'linear' }}
          style={{ width: p.width, height: p.height }}
        />
      ))}

      <div className="featured-header">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="section-title"
        >
          Featured Projects
        </motion.h2>
      </div>

      <div className="featured-content">
        {isMobile ? (
          <div className="mobile-project-list">
            {projects.map((project) => (
              <article className="mobile-project-item" key={project.id}>
                <div className="mobile-project-icon" style={{ color: project.color }}>
                  {React.cloneElement(project.icon, { className: 'w-6 h-6' })}
                </div>
                <div className="mobile-project-copy">
                  <span className="mobile-project-category">{project.category}</span>
                  <h3>{project.title}</h3>
                  <p>{project.description}</p>
                  <a href={project.github} target="_blank" rel="noopener noreferrer">
                    <Github className="w-3.5 h-3.5" /> Codebase
                  </a>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="featured-grid">
            {projects.map((project, idx) => (
              <motion.button
                type="button"
                key={project.id}
                className={`featured-card ${activeProject === idx ? 'active' : ''}`}
                onClick={() => selectProject(idx)}
                aria-pressed={activeProject === idx}
                whileHover={{ y: -4 }}
              >
                <div className="glass-icon-bubble" style={{ borderColor: project.color }}>
                  <div className="glass-icon-inner" style={{ color: project.color }}>
                    {React.cloneElement(project.icon, { className: 'w-6 h-6' })}
                  </div>
                </div>
                <div className="card-copy">
                  <span className="card-category">{project.subtitle}</span>
                  <h3 className="card-title">{project.title}</h3>
                </div>
              </motion.button>
            ))}
          </div>
        )}

        {!isMobile && (
          <div className="featured-preview">
            <AnimatePresence mode="wait">
              <motion.div
                key={projects[activeProject].id}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="preview-card"
              >
                <span className="preview-category">{projects[activeProject].category}</span>
                <h3 className="preview-title">{projects[activeProject].title}</h3>
                <p className="preview-description">{projects[activeProject].description}</p>
                <div className="preview-tech">
                  {projects[activeProject].technologies.map((tech) => (
                    <span key={tech} className="preview-pill">{tech}</span>
                  ))}
                </div>
                <div className="preview-actions">
                  <a
                    href={projects[activeProject].github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="preview-button preview-button-primary"
                  >
                    <Github className="w-4 h-4" /> Codebase
                  </a>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        )}
      </div>
    </section>
  );
};

export default FeaturedProjects;
